import { useUnit } from 'effector-react';
import {
  Button,
  Card,
  Checkbox,
  Popover,
  Radio,
  Select,
  Slider,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import Icon, {
  CheckOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import style from './MapGradient.module.css';
import {
  $activeFilters,
  $chartFilters,
  changeActiveFilterEv,
} from '../../models/activeFiltersModel/index.js';
import {
  $blockedBuckets,
  $bucketAlgorithm,
  $gradientBuckets,
  changeBucketAlgorithmEv,
} from '../../models/gradientModel/index.js';
import { showLoaderEv } from '../../models/webSocketModel/index.js';
import {
  $hexagonsHeight,
  $layersOpacity,
  $mapLoaded,
  changeHexagonsHeightEv,
  changeLayersOpacityEv,
  $invertHeight,
  changeInvertHeightEv,
} from '../../models/mapModel/index.js';
import {
  $isBot,
  $isDarkTheme,
  $language,
  $userInfo,
} from '../../models/authModel/index.js';
import { ru_en_page_dictionary } from '../../dictionaries/ru_en_page_dictionary.js';
import { $isExtrusion } from '../../models/zoom10Model/index.js';
import index_names_dynamics from '../../dictionaries/index_names_dymanics.json';
import LayersController from '../LayersController/LayersController.jsx';
import { public_index_dictionary } from '../../dictionaries/public_index_dictionary.js';
import { $activeLayers } from '../../models/tezberModel/index.js';
import {
  $secondGradient,
  changeSecondGradientEv,
} from '../../models/rbpLandingModel/index.js';
import {
  $hexLabel,
  $isPreset,
  $showRoads,
  changeHexLabelEv,
} from '../../models/rbpLiteModel/index.js';
import RBP_LIte_index_help from '../../dictionaries/RBP_Lite_dict.json';
import { formatNumberCompact } from '../../utils/format-utils.js';
import {
  $tourOpen,
  incStepEv,
  setGradientRefEv,
} from '../../models/educationModel/index.js';
import {
  $hexProp,
  $priceType,
  togglePriceTypeEv,
} from '../../models/centerHomeModel/index.js';
import { ch_title_dict } from '../../data/ch_title_dict.js';
import useWindowSize from '../../hooks/useWindowSize.js';

const algorithm_options = [
  {
    label: 'Equal Intervals (EQI)',
    value: 'EQI',
  },
  {
    label: 'Jenks (JNK)',
    value: 'JNK',
  },
  {
    label: 'Quantiles (QNT)',
    value: 'QNT',
  },
];

function OptionsSvg() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={19} height={18} fill="none">
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M8 3h9.75M8 3a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m0 0H1.25M8 15h9.75M8 15a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m0 0H1.25M14 9h3.75M14 9a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0m0 0H1.25"
      />
    </svg>
  );
}

function OptionsIcon() {
  return <Icon style={{ transform: 'scale(1.3)' }} component={OptionsSvg} />;
}

const radioButtons = [
  {
    label: RBP_LIte_index_help['5'].name_ru,
    value: 'd5',
  },
  {
    label: RBP_LIte_index_help['1'].name_ru,
    value: 'd1',
  },
  {
    label: RBP_LIte_index_help['6'].name_ru,
    value: 'd6',
  },
  {
    label: RBP_LIte_index_help['2'].name_ru,
    value: 'd2',
  },
  {
    label: RBP_LIte_index_help['3'].name_ru,
    value: 'd3',
  },
  {
    label: RBP_LIte_index_help['4'].name_ru,
    value: 'd4',
  },
  {
    label: RBP_LIte_index_help['7'].name_ru,
    value: 'd7',
  },
  {
    label: RBP_LIte_index_help['8'].name_ru,
    value: 'd8',
  },
  {
    label: RBP_LIte_index_help['9'].name_ru,
    value: 'd9',
  },
];

export default function MapGradient() {
  const mapLoaded = useUnit($mapLoaded);
  const gradientBuckets = useUnit($gradientBuckets);
  const activeGradientFilter = useUnit($activeFilters).gradient;
  const activeFilters = useUnit($activeFilters);
  const showLoader = useUnit(showLoaderEv);
  const blockedBuckets = useUnit($blockedBuckets);
  const bucketAlgorithm = useUnit($bucketAlgorithm);
  const changeBucketAlgorithm = useUnit(changeBucketAlgorithmEv);
  const userInfo = useUnit($userInfo);
  const activeAlgorithm = useUnit($bucketAlgorithm);
  const language = useUnit($language);
  const layersOpacity = useUnit($layersOpacity);
  const changeLayersOpacity = useUnit(changeLayersOpacityEv);
  const isExtrusion = useUnit($isExtrusion);
  const changeHexagonsHeight = useUnit(changeHexagonsHeightEv);
  const hexagonsHeight = useUnit($hexagonsHeight);
  const invertHeight = useUnit($invertHeight);
  const changeInvertHeight = useUnit(changeInvertHeightEv);
  const chartFilters = useUnit($chartFilters);
  const { pathname } = useLocation();
  const hideNumbers =
    activeFilters.chosen_metrics[0] === 'income_avg_range' ||
    activeFilters.chosen_metrics[0] === 'rent_price_range';
  const isDark = useUnit($isDarkTheme);
  const activeLayers = useUnit($activeLayers);
  const secondGradient = useUnit($secondGradient);
  const changeSecondGradient = useUnit(changeSecondGradientEv);
  const containerRef = useRef();
  const titleRef = useRef();
  const showRoads = useUnit($showRoads);
  const isPreset = useUnit($isPreset);

  const gradientRef = useRef();
  const setGradientRef = useUnit(setGradientRefEv);

  const [blockLevel, setBlockLevel] = useState('zoom_8');

  const bucketsCopy = gradientBuckets.buckets;

  const changeActiveFilter = useUnit(changeActiveFilterEv);
  const changeHexLabel = useUnit(changeHexLabelEv);
  const hexLabel = useUnit($hexLabel);
  const hexProp = useUnit($hexProp);
  const isBot = useUnit($isBot);
  const priceType = useUnit($priceType);
  const togglePriceType = useUnit(togglePriceTypeEv);
  const [width, height] = useWindowSize();
  const [expanded, setExpanded] = useState(false);

  const onChangeAlgorithm = (value) => {
    changeBucketAlgorithm(value);
  };

  const onInvertChange = (e) => {
    changeInvertHeight(e.target.checked);
  };

  const optionsContent = (
    <div className={style.options_popover}>
      {pathname !== '/tezber' ? (
        <>
          <div className={style.option_title}>
            {ru_en_page_dictionary.algorithm[language]}
          </div>
          <Select
            className={style.options_algorithm}
            options={algorithm_options}
            value={bucketAlgorithm}
            onChange={onChangeAlgorithm}
          />
        </>
      ) : (
        ''
      )}
      <div className={style.option_title}>
        {ru_en_page_dictionary.opacity[language]}
      </div>
      <Slider
        min={0}
        max={1}
        step={0.1}
        defaultValue={0.5}
        onChange={changeLayersOpacity}
        value={layersOpacity}
      />
      {pathname === '/tezber' ? (
        <div onClick={changeSecondGradient} style={{ cursor: 'pointer' }}>
          Переключить градиент
        </div>
      ) : null}
      {pathname !== '/tezber' ? (
        <>
          <div className={style.option_title}>
            {ru_en_page_dictionary.hex_height[language]}
          </div>
          <Slider
            disabled={!isExtrusion}
            min={0}
            max={1000}
            step={100}
            onChange={changeHexagonsHeight}
            value={hexagonsHeight}
          />
          <Checkbox
            checked={invertHeight}
            onChange={onInvertChange}
            disabled={!isExtrusion}
          >
            {ru_en_page_dictionary.invert_height[language]}
          </Checkbox>
        </>
      ) : (
        ''
      )}
    </div>
  );

  useEffect(() => {
    if (window.map) {
      if (mapLoaded) {
        window.map.on('render', () => {
          const zoom = window.map.getZoom();
          let level;
          if (zoom < 11) {
            level = 'zoom_8';
          } else if (zoom >= 11 && zoom < 13) {
            level = 'zoom_9';
          } else {
            level = 'zoom_10';
          }
          if (blockLevel !== level) {
            setBlockLevel(level);
          }
        });
      }
    }
  }, [mapLoaded]);

  useEffect(() => {
    if (gradientRef.current) {
      setGradientRef({
        element: 'gradient',
        ref: gradientRef.current,
      });
    }
  }, [gradientRef]);

  let title;
  if (pathname === '/public') {
    title = public_index_dictionary[activeFilters.chosen_metrics[0]];
  } else if (pathname === '/center-home') {
    title =
      ch_title_dict[`${hexProp}${priceType === 'absolute' ? '_full' : ''}`];
  } else if (pathname === '/tezber') {
    title = activeLayers.includes('active') ? 'Экономически активное' : 'Общее';
  } else if (pathname === '/lite') {
    if (isPreset) {
      title =
        RBP_LIte_index_help[activeFilters.chosen_metrics[0].replace('d', '')][
          `name_${language}`
        ];
    } else {
      title = public_index_dictionary[activeFilters.chosen_metrics[0]];
    }
    if (showRoads) title = 'Среднесуточная проходимость';
  } else if (activeFilters.chosen_metrics.length > 0) {
    title = activeFilters.chosen_metrics.some((item) => item.includes('index'))
      ? index_names_dynamics[activeFilters.chosen_metrics[0].split('_')[1]][
          `name_${language}`
        ]
      : ru_en_page_dictionary[activeFilters.chosen_metrics]
        ? ru_en_page_dictionary[activeFilters.chosen_metrics][language]
        : '';
  } else if (chartFilters.length > 0) {
    title =
      index_names_dynamics[`d${chartFilters[0].chart}`][`name_${language}`];
  } else {
    title = ru_en_page_dictionary.resilience_index[language];
  }

  const labelsContent = (
    <div>
      <Checkbox.Group
        options={radioButtons}
        value={hexLabel}
        onChange={changeHexLabel}
        style={{ display: 'flex', flexDirection: 'column', rowGap: 10 }}
      />
      {/* <Radio.Group */}
      {/*  style={{ display: 'flex', flexDirection: 'column', rowGap: 10 }} */}
      {/*  value={hexLabel} */}
      {/*  onChange={changeHexLabel} */}
      {/* > */}
      {/*  {radioButtons.map((item, index) => { */}
      {/*    return ( */}
      {/*      <Radio key={`hexLabel-${index}`} value={item.value}> */}
      {/*        {item.title} */}
      {/*      </Radio> */}
      {/*    ); */}
      {/*  })} */}
      {/* </Radio.Group> */}
    </div>
  );

  if (width < 500 && !expanded) {
    return (
      <div
        className={style.bottom_bar_wrapper}
        style={{ justifyContent: 'center' }}
      >
        <Card className={style.gradient_wrapper}>
          Условные обозначения
          <Button
            className={style.mobile_expand_button}
            shape="circle"
            icon={<UpOutlined />}
            onClick={() => setExpanded(true)}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={style.bottom_bar_wrapper} ref={gradientRef}>
      {pathname === '/lite' && isPreset ? (
        <Card className={style.labels_wrapper}>
          <div className={style.labels_title}>Подписи ячеек</div>
          <Popover content={labelsContent} trigger="click">
            <Button
              className={style.labels_button}
              icon={<UpOutlined />}
              iconPosition="end"
              id={isBot ? 'labels_button' : ''}
            >
              {hexLabel.length > 0
                ? RBP_LIte_index_help[hexLabel[0].replace('d', '')].name_ru
                : '-'}
            </Button>
          </Popover>
        </Card>
      ) : (
        ''
      )}
      <Card
        className={style.gradient_wrapper}
        bodyStyle={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {pathname === '/center-home' && width < 500 ? (
          <Button
            className={style.mobile_close_button}
            shape="circle"
            icon={<DownOutlined />}
            onClick={() => setExpanded(false)}
          />
        ) : null}
        <div>
          <div
            className={style.gradient_top}
            style={{
              justifyContent:
                pathname !== '/public' || pathname === '/lite'
                  ? 'flex-start'
                  : 'center',
            }}
          >
            {pathname !== '/public' &&
            pathname !== '/tezber' &&
            pathname !== '/lite' &&
            pathname !== '/center-home' ? (
              <LayersController />
            ) : (
              ''
            )}
            <div className={style.gradient_by_wrapper}>
              {/* {ru_en_page_dictionary.gradient_by[language]}:{' '} */}
              <Tooltip title={title} placement="top">
                <div
                  style={{
                    color: isDark ? 'white' : 'black',
                  }}
                  className={style.gradient_title}
                  ref={titleRef}
                >
                  {title}
                </div>
              </Tooltip>
              {pathname === '/center-home' ? (
                <div className={style.chh_switch}>
                  <Switch
                    size="small"
                    checked={priceType === 'absolute'}
                    onChange={togglePriceType}
                  />{' '}
                  {priceType === 'absolute'
                    ? 'Показать стоимость кв метра'
                    : 'Показать стоимость квартир'}
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
          {/* <div */}
          {/*  style={{ */}
          {/*    display: 'flex', */}
          {/*    justifyContent: 'space-between', */}
          {/*    width: '100%', */}
          {/*  }} */}
          {/* > */}
          {/*  {bucketsCopy.map((item) => { */}
          {/*    return ( */}
          {/*      <div style={{ fontSize: 10 }}> */}
          {/*        {new Intl.NumberFormat('ru-RU', { */}
          {/*          notation: 'compact', */}
          {/*        }).format(item)} */}
          {/*      </div> */}
          {/*    ); */}
          {/*  })} */}
          {/* </div> */}
          <Space
            size="small"
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: width < 500 ? 'wrap' : 'nowrap',
              rowGap: 20,
            }}
          >
            {hideNumbers && (
              <div className={style.bucket_wrapper}>
                <div
                  className={style.bucket}
                  style={{ color: isDark ? 'white' : 'black' }}
                >
                  Низкий
                </div>
              </div>
            )}
            {bucketsCopy.map((item, index) => {
              if (index !== bucketsCopy.length - 1) {
                return (
                  <>
                    {!hideNumbers && (
                      <div className={style.bucket_wrapper}>
                        <div
                          className={style.bucket}
                          style={{ color: isDark ? 'white' : 'black' }}
                        >
                          {formatNumberCompact(
                            item,
                            activeFilters.chosen_metrics[0]
                          )}
                        </div>
                      </div>
                    )}
                    <GradientItem
                      key={`${item}${index}`}
                      maxValue={bucketsCopy[index + 1] || 0}
                      minValue={item}
                      color={gradientBuckets.colors[index]}
                      changeActiveFilter={changeActiveFilter}
                      activeGradientFilter={activeGradientFilter}
                      showLoader={showLoader}
                      blockedBuckets={blockedBuckets[blockLevel]}
                      isLast={index === bucketsCopy.length - 2}
                    />
                    {index === bucketsCopy.length - 2 && !hideNumbers && (
                      <div className={style.bucket_wrapper}>
                        <div
                          className={style.bucket}
                          style={{ color: isDark ? 'white' : 'black' }}
                        >
                          {bucketsCopy[index + 1] === Infinity
                            ? '∞'
                            : formatNumberCompact(
                                bucketsCopy[index + 1],
                                activeFilters.chosen_metrics[0]
                              )}
                        </div>
                      </div>
                    )}
                  </>
                );
              }
            })}
            {hideNumbers && (
              <div className={style.bucket_wrapper}>
                <div
                  className={style.bucket}
                  style={{ color: isDark ? 'white' : 'black' }}
                >
                  Высокий
                </div>
              </div>
            )}
          </Space>
        </div>
      </Card>
      {pathname !== '/public' &&
      // pathname !== '/tezber' &&
      pathname !== '/lite' &&
      pathname !== '/center-home' ? (
        <Popover
          content={optionsContent}
          trigger="click"
          getPopupContainer={() => containerRef.current}
        >
          <Button
            icon={<OptionsIcon />}
            className={style.options_button}
            ref={containerRef}
          />
        </Popover>
      ) : (
        ''
      )}
    </div>
  );
}

function GradientItem({
  color,
  maxValue,
  minValue,
  changeActiveFilter,
  activeGradientFilter,
  blockedBuckets,
  showLoader,
  isLast,
}) {
  const tourOpen = useUnit($tourOpen);
  const incStep = useUnit(incStepEv);
  const { pathname } = useLocation();

  const onClick = () => {
    // showLoader();
    if (pathname !== '/public' && pathname !== '/center-home') {
      changeActiveFilter({
        field: 'gradient',
        value: {
          min: minValue,
          max: maxValue,
        },
      });
    }
    // if (tourOpen) {
    //   incStep();
    // }
  };

  return (
    <div
      className={[
        style.gradient_item,
        activeGradientFilter.some(
          (item) => item.min === minValue && item.max === maxValue
        )
          ? style.active
          : '',
        // !blockedBuckets[`${minValue}-${maxValue}`] ? style.blocked : '',
      ].join(' ')}
      style={{
        background: color,
        cursor:
          window.location.pathname === '/center-home' ? 'default' : 'pointer',
        boxShadow: window.location.pathname === '/center-home' ? 'none' : '',
      }}
      // onClick={!blockedBuckets[`${minValue}-${maxValue}`] ? () => {} : onClick}
      onClick={onClick}
    >
      {/* <div className={style.minValue}> */}
      {/*  {new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format( */}
      {/*    minValue */}
      {/*  )} */}
      {/* </div> */}
      {/* {isLast && ( */}
      {/*  <div className={style.maxValue}> */}
      {/*    {new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format( */}
      {/*      maxValue */}
      {/*    )} */}
      {/*  </div> */}
      {/* )} */}
      <div
        className={style.badge}
        style={{
          opacity: activeGradientFilter.some(
            (item) => item.min === minValue && item.max === maxValue
          )
            ? 1
            : 0,
        }}
      >
        <CheckOutlined style={{ fontSize: '13px' }} />
      </div>
    </div>
  );
}
